import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Activity, ChevronRight, ChevronLeft, Pause, Play, RefreshCw, Filter,
  Search, Download, ExternalLink, X, AlertOctagon, Trash2, ArrowDownWideNarrow,
  WifiOff, Wifi, Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLiveAuditFeed } from "@/hooks/useLiveAuditFeed";
import {
  applyFilters, categoryOptions, severityChip, severityDot, outcomeText,
  formatTime, eventsToCsv, downloadBlob,
} from "@/components/liveAudit/audit-utils";
import { useAuth } from "@/context/AuthContext";

/**
 * Live Operations Console — permanent, dockable right-side rail. Streams
 * audit events from the platform-wide broker. Complements the full-page
 * workspace at `/audit/live` (identical data source, expanded controls).
 *
 * Visibility rules
 * ----------------
 * - Only rendered for roles with `audit:read` permission (see hook).
 * - Hidden on the `/audit/live` page (avoid duplicated stream + reserved UI).
 * - User can dock/undock; state is persisted in localStorage.
 */
const STORAGE_KEY = "veralok.liveOps.dockOpen";
const AUDIT_READ_ROLES = new Set([
  "system_admin", "compliance_officer", "auditor", "security_officer", "executive_viewer",
]);

export default function LiveOpsConsole() {
  const location = useLocation();
  const { user } = useAuth();
  const authorized = !!user && AUDIT_READ_ROLES.has(user.role);
  const hiddenOnRoute = location.pathname.startsWith("/audit/live");
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem(STORAGE_KEY) === "true"; }
    catch { return false; }
  });

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, String(open)); } catch (e) { console.warn("[live-ops] localStorage unavailable", e); }
  }, [open]);

  if (!authorized || hiddenOnRoute) return null;

  return (
    <>
      {!open && <RailHandle onOpen={() => setOpen(true)} />}
      {open && <RailPanel onClose={() => setOpen(false)} />}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Collapsed handle — always visible when console is docked closed   */
/* ------------------------------------------------------------------ */

function RailHandle({ onOpen }) {
  const feed = useLiveAuditFeed({ capacity: 200 });
  const criticalCount = useMemo(
    () => feed.events.filter((e) => e.risk_level === "critical" || e.risk_level === "high").length,
    [feed.events],
  );
  return (
    <button
      data-testid="live-ops-handle"
      onClick={onOpen}
      className="fixed right-0 top-1/2 -translate-y-1/2 z-40 h-40 w-8 bg-card border border-r-0 border-border rounded-l-sm flex flex-col items-center justify-between py-3 hover:bg-secondary/60 transition-colors group"
      aria-label="Open Live Operations Console"
    >
      <StatusDot status={feed.status} />
      <div className="flex-1 flex flex-col items-center justify-center gap-2">
        <Activity className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
        <div className="[writing-mode:vertical-rl] rotate-180 text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-foreground">
          Live Ops
        </div>
      </div>
      {criticalCount > 0 && (
        <span data-testid="live-ops-handle-badge" className="min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[9px] font-mono flex items-center justify-center">
          {criticalCount > 99 ? "99+" : criticalCount}
        </span>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Docked panel                                                      */
/* ------------------------------------------------------------------ */

function RailPanel({ onClose }) {
  const navigate = useNavigate();
  const feed = useLiveAuditFeed({ capacity: 400 });
  const [autoScroll, setAutoScroll] = useState(true);
  const [filters, setFilters] = useState({ search: "", severity: "", category: "" });
  const [showFilters, setShowFilters] = useState(false);
  const listRef = useRef(null);

  const filtered = useMemo(() => applyFilters(feed.events, filters), [feed.events, filters]);

  // Keep newest events visible if auto-scroll is on and not paused.
  useEffect(() => {
    if (autoScroll && listRef.current && !feed.paused) {
      listRef.current.scrollTop = 0;
    }
  }, [filtered.length, autoScroll, feed.paused]);

  const doExport = useCallback(() => {
    downloadBlob(`veralok_live_${Date.now()}.csv`, "text/csv", eventsToCsv(filtered));
  }, [filtered]);

  return (
    <aside
      data-testid="live-ops-console"
      className="fixed right-0 top-0 bottom-0 z-40 w-[380px] bg-card border-l border-border shadow-[0_0_40px_-10px_rgba(0,0,0,0.5)] flex flex-col animate-slide-in-right"
      style={{ animation: "slideInRight 200ms ease-out" }}
    >
      <header className="h-14 border-b border-border flex items-center px-4 gap-2 shrink-0">
        <Activity className="h-4 w-4" />
        <div className="flex-1">
          <div className="font-display text-sm leading-none">Live Ops Console</div>
          <div className="flex items-center gap-1.5 mt-1">
            <StatusDot status={feed.status} />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
              {statusLabel(feed.status)}
            </span>
          </div>
        </div>
        <IconButton title="Filters" testId="live-ops-toggle-filters" active={showFilters} onClick={() => setShowFilters((v) => !v)}>
          <Filter className="h-3.5 w-3.5" />
        </IconButton>
        <IconButton title="Expand full page" testId="live-ops-expand" onClick={() => navigate("/audit/live")}>
          <ExternalLink className="h-3.5 w-3.5" />
        </IconButton>
        <IconButton title="Close" testId="live-ops-close" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </IconButton>
      </header>

      {/* Toolbar */}
      <div className="border-b border-border px-3 py-2 flex flex-wrap items-center gap-1.5 shrink-0">
        <button
          data-testid={feed.paused ? "live-ops-resume" : "live-ops-pause"}
          onClick={feed.paused ? feed.resume : feed.pause}
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[11px] uppercase tracking-wider transition-colors",
            feed.paused
              ? "border-warning/40 text-warning bg-warning/5 hover:bg-warning/10"
              : "border-border hover:bg-secondary"
          )}
        >
          {feed.paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
          {feed.paused ? "Resume" : "Pause"}
        </button>
        {feed.paused && feed.bufferedDuringPause > 0 && (
          <span data-testid="live-ops-paused-badge" className="text-[10px] uppercase tracking-wider font-mono text-warning">
            +{feed.bufferedDuringPause} buffered
          </span>
        )}
        <button
          data-testid="live-ops-refresh"
          onClick={feed.refresh}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border hover:bg-secondary text-[11px] uppercase tracking-wider"
          title="Refresh hydrate"
        >
          <RefreshCw className="h-3 w-3" />
        </button>
        <button
          data-testid="live-ops-autoscroll"
          onClick={() => setAutoScroll((v) => !v)}
          className={cn(
            "inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[11px] uppercase tracking-wider",
            autoScroll ? "border-foreground text-foreground" : "border-border text-muted-foreground hover:bg-secondary"
          )}
          title="Auto-scroll to newest"
        >
          <ArrowDownWideNarrow className="h-3 w-3" />
          Auto
        </button>
        <button
          data-testid="live-ops-export"
          onClick={doExport}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border hover:bg-secondary text-[11px] uppercase tracking-wider"
          title="Export visible events (CSV)"
        >
          <Download className="h-3 w-3" />
        </button>
        <button
          data-testid="live-ops-clear"
          onClick={feed.clear}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border hover:bg-secondary text-[11px] uppercase tracking-wider text-muted-foreground"
          title="Clear local buffer (does not touch ledger)"
        >
          <Trash2 className="h-3 w-3" />
        </button>
        <div className="ml-auto text-[10px] font-mono text-muted-foreground tabular">
          {filtered.length}/{feed.events.length}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="border-b border-border p-3 space-y-2 shrink-0 bg-background/50">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <input
              data-testid="live-ops-search"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              placeholder="Search action, actor, target…"
              className="w-full h-8 pl-7 pr-2 rounded-sm bg-background border border-border text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <div className="flex gap-2">
            <select
              data-testid="live-ops-severity"
              value={filters.severity}
              onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))}
              className="h-8 rounded-sm border border-border bg-background text-xs px-2 flex-1"
            >
              <option value="">All severity</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <select
              data-testid="live-ops-category"
              value={filters.category}
              onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
              className="h-8 rounded-sm border border-border bg-background text-xs px-2 flex-1"
            >
              <option value="">All categories</option>
              {categoryOptions().map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Event list */}
      <div ref={listRef} data-testid="live-ops-list" className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-6 text-center">
            {feed.status === "connecting" ? (
              <div className="flex flex-col items-center gap-2 text-muted-foreground text-xs">
                <Loader2 className="h-4 w-4 animate-spin" />
                Establishing secure channel…
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">No events match filters</div>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {filtered.map((e) => (
              <li key={e.ledger_id} data-testid={`live-ops-row-${e.ledger_id}`}>
                <ConsoleRow event={e} />
              </li>
            ))}
          </ul>
        )}
      </div>
      <footer className="h-9 border-t border-border px-3 flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground font-mono shrink-0">
        <span>VeraLok Live Feed</span>
        <span>WSS · {feed.reconnectAttempts > 0 ? `retries ${feed.reconnectAttempts}` : "stable"}</span>
      </footer>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/*  Row (compact) — used inside the rail                              */
/* ------------------------------------------------------------------ */

function ConsoleRow({ event }) {
  const navigate = useNavigate();
  const openTarget = useCallback(() => {
    if (event.target_type === "IDENTITY" && event.target_id) navigate(`/identities/${event.target_id}`);
    else if (event.target_type === "RECOVERY_REQUEST" && event.target_id) navigate(`/recovery/${event.target_id}`);
    else navigate(`/audit/live?ledger_id=${encodeURIComponent(event.ledger_id)}`);
  }, [navigate, event]);

  return (
    <button
      onClick={openTarget}
      className="w-full text-left px-3 py-2 hover:bg-secondary/40 transition-colors block group"
    >
      <div className="flex items-start gap-2">
        <span className="mt-1.5">{severityDot(event.risk_level)}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px]">
            <span className="font-mono text-muted-foreground tabular">{formatTime(event.timestamp)}</span>
            <span className="font-medium truncate">{event.action}</span>
            <span className="ml-auto text-[9px] uppercase tracking-widest text-muted-foreground">{event.category}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
            <span className="font-mono">{event.actor_email || "system"}</span>
            {event.target_id && <>
              <span className="mx-1.5 opacity-40">·</span>
              <span className="font-mono">{event.target_id}</span>
            </>}
          </div>
          {event.outcome !== "SUCCESS" && (
            <div className="mt-0.5">{outcomeText(event.outcome)}</div>
          )}
        </div>
        <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 mt-1" />
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Small helpers                                                     */
/* ------------------------------------------------------------------ */

function IconButton({ children, active, testId, ...rest }) {
  return (
    <button
      data-testid={testId}
      className={cn(
        "h-8 w-8 flex items-center justify-center rounded-sm border transition-colors",
        active ? "border-foreground text-foreground bg-secondary" : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary"
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function StatusDot({ status }) {
  const map = {
    live:          "bg-success animate-pulse",
    connecting:    "bg-warning animate-pulse",
    reconnecting:  "bg-warning animate-pulse",
    offline:       "bg-destructive",
    forbidden:     "bg-muted-foreground",
  };
  return <span data-testid="live-ops-status" data-status={status} className={cn("inline-block h-2 w-2 rounded-full", map[status] || "bg-muted-foreground")} />;
}

export function statusLabel(status) {
  return ({
    live:         "Live",
    connecting:   "Connecting…",
    reconnecting: "Reconnecting…",
    offline:      "Offline",
    forbidden:    "Restricted",
  })[status] || status;
}
